import React, { FC, useEffect, useState } from "react";

import "../styles.scss";
import "../scripts";

import {
  primaryBlock,
  PrimaryBlockConfig,
  getDefaultProps,
  KameleonElement,
  SecondaryBlockSize,
  TertiaryBlockSize,
  BlockTheme,
  renderSecondaryBlock,
  BlockType,
} from "@kameleon-core/lib";
import { Box } from "theme-ui";

import { WebflowComponentProps, webflowHOC } from "@kameleon-common/webflow-js";
import { Variants } from "@kameleon-util/global-theme";
import { v4 as uuidv4 } from "uuid";
import blockinfo from "./blockinfo.json";
import defaults from "./defaults.json";
import { EyecatcherType, layoutOptions } from "./types/prop-types";
import getEvents from "./js/webflow";

/** Eyecatcher Primary */
const blockConfig: PrimaryBlockConfig = {
  secondaryBlockSize: SecondaryBlockSize.ExtraSmall,
  tertiaryBlockSize: TertiaryBlockSize.None,
  blockType: BlockType.Primary,
  blockTheme: BlockTheme.light,
};

type PassThroughProps = {
  flyingImagesId: string;
};
type EyecatcherWebflowType = WebflowComponentProps<
  EyecatcherType,
  PassThroughProps
>;

const EyecatcherComponent: FC<EyecatcherWebflowType> = ({
  SecondaryBlock,
  tagline,
  header,
  description,
  images,
  passThrough,
}: EyecatcherWebflowType) => {
  const [flyingImagesId] = useState<string>(uuidv4());

  useEffect(() => {
    passThrough?.({ flyingImagesId });
  }, []);

  return (
    <Box className="primary-eyecatcher-centered-flying-images-v1">
      <Box className="layout421_component">
        <Box className="layout421_content">
          <Box className="padding-global">
            <Box className="container-medium">
              <Box className="padding-section-large">
                <Box className="text-align-center">
                  {tagline && (
                    <Box className="margin-bottom margin-xsmall tagline">
                      <Variants.TagLine>{tagline}</Variants.TagLine>
                    </Box>
                  )}
                  <Box className="margin-bottom margin-small">
                    <Variants.Heading>{header}</Variants.Heading>
                  </Box>
                  {description && (
                    <Variants.ParagraphMedium>
                      {description}
                    </Variants.ParagraphMedium>
                  )}
                  {renderSecondaryBlock(SecondaryBlock)}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box className="layout421_content-bottom">
          <Box className="layout421_images-wrapper">
            {images.map((image, i) => (
              <Box
                key={i}
                className={`layout421_image-wrapper is-image${i + 1}`}
              >
                <Variants.Image
                  src={image.url}
                  loading="lazy"
                  alt={image.alt}
                  className="layout421_image"
                />
              </Box>
            ))}
          </Box>
        </Box>
        <Box data-w-id={flyingImagesId} className="layout421_ix-trigger" />
      </Box>
    </Box>
  );
};
const ComponentWithWebflow = webflowHOC<
  EyecatcherWebflowType,
  PassThroughProps
>(EyecatcherComponent, [
  {
    module: "ix2",
    getEvents: (pageId: any, passThrough): any =>
      getEvents(pageId, passThrough.flyingImagesId),
  },
]);

export const Eyecatcher = primaryBlock<EyecatcherType>(
  blockConfig,
  ComponentWithWebflow
);

Eyecatcher.defaultProps = getDefaultProps<EyecatcherType>(defaults);

export const Element: KameleonElement = {
  component: Eyecatcher,
  blockInfo: blockinfo,
  defaultProps: {
    layoutProps: defaults.layoutProps,
    contentProps: defaults.contentProps,
  },
  layoutOptions,
};
